import { Input } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components/macro'
import { FOOD_SAFETY_SECONDARY_TEXT } from 'src/constants/foodSafety'
import {
  buildFoodSafetyLoggingProductSuggestions,
  buildFoodSafetyShoppingListSuggestions,
  FOOD_SAFETY_SHOPPING_LIST_LOGGING_LIMIT,
  FOOD_SAFETY_SHOPPING_LIST_SEARCH_LIMIT,
  FOOD_SAFETY_SHOPPING_LIST_SETUP_LIMIT,
  FOOD_SAFETY_SUGGEST_EMPTY_SEARCH_CUE,
  filterFoodSafetySuggestionsByQuery,
  isFoodSafetyShoppingListSuggestSection,
  listFoodSafetyShoppingListCandidates,
  nextFoodSafetySuggestSampleSeed,
} from 'src/utils/foodSafetyShoppingListSuggest'
import { listFoodSafetyRecipeCandidates } from 'src/utils/foodSafetyRecipeSuggest'

const NO_RECIPES = []

/**
 * “From your shopping list” — search + chips for Setup / day composer.
 * Chef-simple: one search input above chips; tap fills name / starts template.
 */
const FoodSafetyShoppingListSuggestBlock = ({
  section,
  products = [],
  /** Cooling: recipe titles replace the shopping list when there are any. */
  recipes = NO_RECIPES,
  existingDefinitions = [],
  templates = [],
  /** 'setup' | 'logging' */
  mode = 'setup',
  limit,
  selectedTitle = '',
  onSelect,
  title = 'From your shopping list',
  /** Appends “optional” so shopping-list picks stay a helper, not the log. */
  markOptional = false,
  hint,
  /** When this changes, query clears and default chips re-sample. */
  resampleKey = '',
  testId,
  chipKind,
  /** Setup tab: mayo card with border. Drawer / composer: bare stack. */
  framed = false,
  /** Cooling day log: recipe chips only. No shopping-list fallback. */
  recipesOnly = false,
  /** Shopping-list block steps aside when recipe chips are shown above Submit. */
  yieldToRecipes = false,
  /** Deliveries: only chips from this supplier. */
  supplierName = '',
}) => {
  const [query, setQuery] = useState('')
  const [sampleSeed, setSampleSeed] = useState(() =>
    nextFoodSafetySuggestSampleSeed(section || 'list'),
  )

  useEffect(() => {
    setQuery('')
    setSampleSeed(nextFoodSafetySuggestSampleSeed(section || 'list'))
  }, [section, resampleKey])

  const resolvedLimit =
    limit != null
      ? limit
      : mode === 'logging'
      ? FOOD_SAFETY_SHOPPING_LIST_LOGGING_LIMIT
      : FOOD_SAFETY_SHOPPING_LIST_SETUP_LIMIT

  const recipeCandidates = useMemo(() => {
    const asDefs =
      mode === 'logging'
        ? (templates || []).map(t => ({ section, name: t?.name, active: true }))
        : []
    return listFoodSafetyRecipeCandidates({
      recipes,
      existingDefinitions: [...(existingDefinitions || []), ...asDefs],
      section,
    })
  }, [recipes, existingDefinitions, templates, section, mode])
  const fromRecipes = recipesOnly || recipeCandidates.length > 0

  const listCandidateCount = useMemo(() => {
    if (!isFoodSafetyShoppingListSuggestSection(section)) return 0
    if (mode === 'logging') {
      const asDefs = (templates || []).map(t => ({
        section,
        name: t?.name,
        active: true,
      }))
      return listFoodSafetyShoppingListCandidates({
        products,
        existingDefinitions: [...(existingDefinitions || []), ...asDefs],
        section,
        supplierName,
      }).length
    }
    return listFoodSafetyShoppingListCandidates({
      products,
      existingDefinitions,
      section,
      supplierName,
    }).length
  }, [products, existingDefinitions, templates, section, mode, supplierName])

  const candidateCount = fromRecipes
    ? recipeCandidates.length
    : listCandidateCount

  const suggestions = useMemo(() => {
    if (!isFoodSafetyShoppingListSuggestSection(section)) return []
    if (fromRecipes) {
      const q = String(query || '').trim()
      return q
        ? filterFoodSafetySuggestionsByQuery(recipeCandidates, q).slice(
            0,
            Math.max(resolvedLimit, FOOD_SAFETY_SHOPPING_LIST_SEARCH_LIMIT),
          )
        : recipeCandidates.slice(0, resolvedLimit)
    }
    if (mode === 'logging') {
      return buildFoodSafetyLoggingProductSuggestions({
        products,
        templates,
        existingDefinitions,
        section,
        limit: resolvedLimit,
        query,
        sampleSeed,
        supplierName,
      })
    }
    return buildFoodSafetyShoppingListSuggestions({
      products,
      existingDefinitions,
      section,
      limit: resolvedLimit,
      query,
      sampleSeed,
      supplierName,
    })
  }, [
    fromRecipes,
    recipeCandidates,
    products,
    existingDefinitions,
    templates,
    section,
    mode,
    resolvedLimit,
    query,
    sampleSeed,
    supplierName,
  ])

  if (!isFoodSafetyShoppingListSuggestSection(section)) return null
  if (yieldToRecipes && recipeCandidates.length > 0) return null
  if (
    recipesOnly &&
    recipeCandidates.length === 0 &&
    !String(query || '').trim()
  ) {
    return null
  }
  if (candidateCount === 0 && !String(query || '').trim()) return null

  const selectedKey = String(selectedTitle || '')
    .trim()
    .toLowerCase()
  const trimmedQuery = String(query || '').trim()

  const body = (
    <>
      {title ? (
        <Title
          data-mode={mode}
          data-testid={testId ? `${testId}-title` : undefined}
        >
          {`${fromRecipes ? 'From your recipes' : title}${
            markOptional ? ' · optional' : ''
          }`}
        </Title>
      ) : null}
      {hint && !fromRecipes ? <Hint>{hint}</Hint> : null}
      <SearchInput
        allowClear
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder={fromRecipes ? 'Search recipes' : 'Search shopping list'}
        data-testid={testId ? `${testId}-search` : 'fs-list-suggest-search'}
        aria-label={fromRecipes ? 'Search recipes' : 'Search shopping list'}
      />
      {suggestions.length > 0 ? (
        <ChipRow>
          {suggestions.map(item => {
            const active =
              selectedKey && item.title.toLowerCase() === selectedKey
            const label = item.cue ? `${item.title} · ${item.cue}` : item.title
            return (
              <Chip
                key={item.id}
                type='button'
                data-kind={chipKind || undefined}
                data-active={active ? 'true' : 'false'}
                onClick={() => onSelect?.(item.title)}
                aria-label={label}
                data-testid={testId ? `${testId}-chip-${item.id}` : undefined}
              >
                {label}
              </Chip>
            )
          })}
        </ChipRow>
      ) : trimmedQuery ? (
        <EmptyHint data-testid={testId ? `${testId}-empty` : undefined}>
          {`${
            fromRecipes ? 'No recipes match' : 'No list products match'
          } “${trimmedQuery}”.`}
        </EmptyHint>
      ) : (
        <EmptyHint data-testid={testId ? `${testId}-empty-search` : undefined}>
          {FOOD_SAFETY_SUGGEST_EMPTY_SEARCH_CUE}
        </EmptyHint>
      )}
    </>
  )

  if (framed) {
    return <Frame data-testid={testId}>{body}</Frame>
  }

  return <Wrap data-testid={testId}>{body}</Wrap>
}

const Frame = styled.div`
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  border-radius: 6px;
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const Title = styled.div`
  font-weight: 600;
  font-size: 0.95rem;

  &[data-mode='logging'] {
    font-size: 0.75rem;
    letter-spacing: 0.02em;
    color: ${FOOD_SAFETY_SECONDARY_TEXT};
  }
`

const Hint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const SearchInput = styled(Input)`
  max-width: 100%;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const Chip = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  background: #fff;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  cursor: pointer;
  font-size: 0.85rem;
  max-width: 100%;
  text-align: left;
  overflow-wrap: anywhere;

  &:hover {
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  }

  &[data-kind='list'] {
    border-style: dashed;
    color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
    padding: 0.25rem 0.75rem;
  }

  &[data-active='true'] {
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
    border-style: solid;
    background: #f3f8e8;
    font-weight: 600;
    color: inherit;
  }
`

const EmptyHint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

export default FoodSafetyShoppingListSuggestBlock
